import React, { useState } from 'react';
import { Contract } from '../../../types/contract';
import { mainCategoryInfo } from '../../../mocks/contracts';

interface ContractPreviewModalProps {
  contract: Contract;
  onClose: () => void;
}

const ContractPreviewModal: React.FC<ContractPreviewModalProps> = ({ contract, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const categoryData = mainCategoryInfo[contract.category];

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/70 z-50 transition-opacity duration-300"
        onClick={onClose}
      ></div>
      
      <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-scale-in">
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-2xl font-bold text-gray-800">契約預覽</h2>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-close-line text-2xl text-gray-600"></i>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="relative bg-gray-100 rounded-xl overflow-hidden mb-4">
                  <img 
                    src={contract.previewImages[currentImageIndex]} 
                    alt={`${contract.name} - 第 ${currentImageIndex + 1} 頁`}
                    className="w-full h-auto"
                  />
                  
                  {contract.previewImages.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentImageIndex(prev => 
                          prev === 0 ? contract.previewImages.length - 1 : prev - 1
                        )}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors duration-300 cursor-pointer shadow-lg"
                      >
                        <i className="ri-arrow-left-s-line text-2xl text-gray-800"></i>
                      </button>
                      
                      <button
                        onClick={() => setCurrentImageIndex(prev => 
                          prev === contract.previewImages.length - 1 ? 0 : prev + 1
                        )}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors duration-300 cursor-pointer shadow-lg"
                      >
                        <i className="ri-arrow-right-s-line text-2xl text-gray-800"></i>
                      </button>
                    </>
                  )}
                </div>

                {contract.previewImages.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {contract.previewImages.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-shrink-0 w-20 h-24 rounded-lg overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                          currentImageIndex === index ? 'border-[#d4af37]' : 'border-gray-200 hover:border-[#d4af37]'
                        }`}
                      >
                        <img 
                          src={img} 
                          alt={`縮圖 ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div 
                  className="inline-block px-3 py-1 rounded-full text-sm font-bold text-white mb-4"
                  style={{ backgroundColor: categoryData.color }}
                >
                  {categoryData.name}
                </div>

                <h3 className="text-3xl font-bold text-gray-800 mb-4">{contract.name}</h3>

                <div className="flex items-center gap-6 mb-6 text-gray-600">
                  <div className="flex items-center gap-2">
                    <i className="ri-file-text-line text-xl"></i>
                    <span>{contract.pages} 頁</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 mb-6">
                  <div className="text-3xl font-bold text-[#d4af37] mb-2">
                    NT$ {contract.price.toLocaleString()}
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-gray-800 mb-3">契約說明</h4>
                  <p className="text-gray-600 leading-relaxed">{contract.description}</p>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-gray-800 mb-3">分類說明</h4>
                  <p className="text-gray-600 leading-relaxed">{categoryData.description}</p>
                </div>

                <a
                  href="#"
                  className="w-full h-14 bg-[#d4af37] text-white font-bold rounded-lg hover:bg-[#c49d2e] transition-colors duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-file-list-3-line text-xl"></i>
                  <span>前往訂購表單</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContractPreviewModal;
